import parse, { type HTMLReactParserOptions } from "html-react-parser";
import DOMPurify from "dompurify";

// 마크다운 문법을 HTML로 변환하는 함수
const convertMarkdownToHtml = (content: string): string => {
  return (
    content
      // **텍스트** → <strong>텍스트</strong>
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      // *텍스트* → <em>텍스트</em>
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      // `코드` → <code>코드</code>
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      // # 제목 → <h3>제목</h3>
      .replace(/^# (.*$)/gm, "<h3>$1</h3>")
      // ## 소제목 → <h4>소제목</h4>
      .replace(/^## (.*$)/gm, "<h4>$1</h4>")
      // ### 소제목 → <h5>소제목</h5>
      .replace(/^### (.*$)/gm, "<h5>$1</h5>")
      // > 인용구 → <blockquote>인용구</blockquote>
      .replace(/^> (.*$)/gm, "<blockquote>$1</blockquote>")
      // - 항목 → <ul><li>항목</li></ul>
      .replace(/^- (.*$)/gm, "<ul><li>$1</li></ul>")
      // 1. 항목 → <ol><li>항목</li></ol>
      .replace(/^\d+\. (.*$)/gm, "<ol><li>$1</li></ol>")
  );
};

interface HtmlRenderProps {
  content: string;
  className?: string;
  style?: React.CSSProperties;
  isUserMessage?: boolean;
}

const HtmlRender: React.FC<HtmlRenderProps> = ({
  content,
  className,
  style,
  isUserMessage = false,
}) => {
  // 마크다운 문법을 HTML로 변환
  const htmlContent = convertMarkdownToHtml(content);

  // DOMPurify로 HTML 정제
  const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "s",
      "del",
      "mark",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "dl",
      "dt",
      "dd",
      "blockquote",
      "pre",
      "code",
      "kbd",
      "samp",
      "var",
      "a",
      "img",
      "figure",
      "figcaption",
      "table",
      "thead",
      "tbody",
      "tfoot",
      "tr",
      "td",
      "th",
      "div",
      "span",
      "section",
      "article",
      "aside",
      "header",
      "footer",
      "main",
      "nav",
      "address",
      "time",
      "cite",
      "q",
      "abbr",
      "acronym",
      "dfn",
      "small",
      "big",
      "sub",
      "sup",
      "ins",
      "del",
      "hr",
      "fieldset",
      "legend",
      "label",
      "input",
      "button",
      "textarea",
      "select",
      "option",
      "optgroup",
      "form",
    ],
    ALLOWED_ATTR: [
      "href",
      "src",
      "alt",
      "title",
      "width",
      "height",
      "class",
      "id",
      "style",
      "target",
      "rel",
      "type",
      "value",
      "name",
      "placeholder",
      "required",
      "disabled",
      "readonly",
      "checked",
      "selected",
      "maxlength",
      "minlength",
      "pattern",
      "autocomplete",
      "autofocus",
      "form",
      "formaction",
      "formenctype",
      "formmethod",
      "formnovalidate",
      "formtarget",
      "list",
      "max",
      "min",
      "step",
      "multiple",
      "size",
      "spellcheck",
      "wrap",
      "rows",
      "cols",
    ],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false,
    SANITIZE_DOM: true,
    WHOLE_DOCUMENT: false,
    FORCE_BODY: false,
    USE_PROFILES: {
      html: true,
      svg: false,
      svgFilters: false,
      mathMl: false,
    },
  });

  // HTML을 React 요소로 파싱하는 옵션
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element) {
        // 링크를 React 컴포넌트로 교체하여 새창에서 열리도록 함
        if (domNode.tagName === "A") {
          const href = domNode.getAttribute("href");

          if (href) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#3182ce",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  window.open(href, "_blank");
                }}
              >
                {domNode.innerHTML}
              </a>
            );
          }
        }

        // 이미지에 lazy loading 추가
        if (domNode.tagName === "IMG") {
          const src = domNode.getAttribute("src");
          const alt = domNode.getAttribute("alt");

          return (
            <img
              src={src || ""}
              alt={alt || ""}
              loading="lazy"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
              }}
            />
          );
        }
      }
    },
  };

  return (
    <div
      className={className}
      style={{
        ...style,
        lineHeight: "1.4",
        wordBreak: "break-word",
        overflowWrap: "break-word",
      }}
    >
      <style>
        {`
          .html-render-content {
            font-size: 14px;
            line-height: 1.4;
            color: inherit;
          }
          .html-render-content.user-message {
            color: white;
          }
          .html-render-content.user-message a {
            color: #90cdf4;
          }
          .html-render-content.user-message a:hover {
            color: #63b3ed;
          }
          .html-render-content.user-message code {
            background-color: rgba(255, 255, 255, 0.2);
            color: white;
          }
          .html-render-content.user-message pre {
            background-color: rgba(255, 255, 255, 0.1);
            color: white;
          }
          .html-render-content.user-message blockquote {
            border-left-color: rgba(255, 255, 255, 0.3);
            background-color: rgba(255, 255, 255, 0.1);
            color: white;
          }
          .html-render-content p {
            margin: 0.5em 0;
            padding: 0;
          }
          .html-render-content h1, .html-render-content h2, .html-render-content h3,
          .html-render-content h4, .html-render-content h5, .html-render-content h6 {
            margin: 0.8em 0 0.4em 0;
            font-weight: 600;
            line-height: 1.2;
          }
          .html-render-content h1 { font-size: 1.4em; }
          .html-render-content h2 { font-size: 1.3em; }
          .html-render-content h3 { font-size: 1.2em; }
          .html-render-content h4 { font-size: 1.1em; }
          .html-render-content h5, .html-render-content h6 { font-size: 1em; }
          .html-render-content ul, .html-render-content ol {
            margin: 0.5em 0;
            padding-left: 1.5em;
          }
          .html-render-content li {
            margin: 0.2em 0;
            line-height: 1.4;
          }
          .html-render-content blockquote {
            margin: 0.5em 0;
            padding: 0.5em 1em;
            border-left: 3px solid #e2e8f0;
            background-color: #f7fafc;
            font-style: italic;
          }
          .html-render-content code {
            background-color: #f1f5f9;
            padding: 0.2em 0.4em;
            border-radius: 3px;
            font-family: 'Courier New', monospace;
            font-size: 0.9em;
          }
          .html-render-content pre {
            background-color: #f1f5f9;
            padding: 0.8em;
            border-radius: 6px;
            overflow-x: auto;
            margin: 0.5em 0;
          }
          .html-render-content pre code {
            background: none;
            padding: 0;
          }
          .html-render-content a {
            color: #3182ce;
            text-decoration: underline;
          }
          .html-render-content a:hover {
            color: #2c5282;
          }
          .html-render-content strong, .html-render-content b {
            font-weight: 600;
          }
          .html-render-content em, .html-render-content i {
            font-style: italic;
          }
          .html-render-content table {
            border-collapse: collapse;
            width: 100%;
            margin: 0.5em 0;
          }
          .html-render-content th, .html-render-content td {
            border: 1px solid #e2e8f0;
            padding: 0.5em;
            text-align: left;
          }
          .html-render-content th {
            background-color: #f7fafc;
            font-weight: 600;
          }
          
          /* 문서 요약 스타일 */
          .html-render-content .document-summary {
            background-color: #f8fafc;
            border-radius: 8px;
            padding: 16px;
            margin: 8px 0;
            border: 1px solid #e2e8f0;
          }
          
          .html-render-content .document-summary h3 {
            color: #2d3748;
            margin-bottom: 16px;
            font-size: 1.3em;
            border-bottom: 2px solid #3182ce;
            padding-bottom: 8px;
          }
          
          .html-render-content .document-summary h4 {
            color: #4a5568;
            margin: 16px 0 8px 0;
            font-size: 1.1em;
            font-weight: 600;
          }
          
          .html-render-content .document-summary .summary-main {
            background-color: #ebf8ff;
            border-left: 4px solid #3182ce;
            padding: 12px 16px;
            margin: 12px 0;
            border-radius: 0 6px 6px 0;
          }
          
          .html-render-content .document-summary .summary-details {
            background-color: #f7fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            padding: 12px 16px;
            margin: 12px 0;
          }
          
          .html-render-content .document-summary .summary-conclusion {
            background-color: #f0fff4;
            border-left: 4px solid #38a169;
            padding: 12px 16px;
            margin: 12px 0;
            border-radius: 0 6px 6px 0;
          }
          
          .html-render-content .document-summary p {
            margin: 8px 0;
            line-height: 1.6;
          }
          
          /* 사용자 메시지에서의 문서 요약 스타일 */
          .html-render-content.user-message .document-summary {
            background-color: rgba(255, 255, 255, 0.1);
            border-color: rgba(255, 255, 255, 0.2);
          }
          
          .html-render-content.user-message .document-summary h3 {
            color: white;
            border-bottom-color: rgba(255, 255, 255, 0.3);
          }
          
          .html-render-content.user-message .document-summary h4 {
            color: rgba(255, 255, 255, 0.9);
          }
          
          .html-render-content.user-message .document-summary .summary-main {
            background-color: rgba(59, 130, 246, 0.2);
            border-left-color: #60a5fa;
          }
          
          .html-render-content.user-message .document-summary .summary-details {
            background-color: rgba(255, 255, 255, 0.1);
            border-color: rgba(255, 255, 255, 0.2);
          }
          
          .html-render-content.user-message .document-summary .summary-conclusion {
            background-color: rgba(34, 197, 94, 0.2);
            border-left-color: #4ade80;
          }
        `}
      </style>
      <div
        className={`html-render-content ${isUserMessage ? "user-message" : ""}`}
      >
        {parse(sanitizedHtml, options)}
      </div>
    </div>
  );
};

export default HtmlRender;
